import { useEffect, useMemo } from 'react'
import { IAtlasObjectRef } from '@common/helpers/atlasUrls'
import {
  ATLAS_OBJECT_PREVIEW_BATCH_SIZE,
  IAtlasObjectPreview,
  atlasObjectRefKey,
} from '@common/interfaces/atlasObjects'
import { listAtlasObjectPreviews } from '../redux/atlasLinks/actions'
import { atlasObjectPreviews } from '../redux/atlasLinks/selectors'
import { useDispatch, useSelector } from '../redux/hooks'
import { AppDispatch } from '../redux/types'

// Refs queued this tick, flushed as one request on the next macrotask.
//
// Every rendered chip asks for its own ref, and chips are independent siblings
// spread across a timeline with no shared parent to collect through — so the
// queue is module-level, and a conversation full of links costs one request
// instead of one per chip.
//
// Caveat: `dispatch` comes from whichever hook schedules the flush first. Fine
// with a single store, wrong if two were ever mounted in one process.
const pendingRefs = new Map<string, IAtlasObjectRef>()
let flushTimer: ReturnType<typeof setTimeout> | null = null

const scheduleFlush = (dispatch: AppDispatch) => {
  if (flushTimer) {
    return
  }
  flushTimer = setTimeout(() => {
    flushTimer = null
    const refs = [...pendingRefs.values()]
    pendingRefs.clear()
    // Chunked to what the server will answer: the reducer marks every submitted
    // ref as requested, so a surplus would be permanently left unresolved.
    for (let index = 0; index < refs.length; index += ATLAS_OBJECT_PREVIEW_BATCH_SIZE) {
      void dispatch(
        listAtlasObjectPreviews(refs.slice(index, index + ATLAS_OBJECT_PREVIEW_BATCH_SIZE)),
      )
    }
  }, 0)
}

/**
 * Resolves Atlas object refs to the record behind each rendered link, fetching
 * any not cached yet. Keyed `Type:id`.
 *
 * A key absent from the returned map is still in flight or does not resolve at
 * all — callers must render a fallback rather than wait for one. A key present
 * with `restricted: true` is a record this reader may not see, which is a
 * different outcome and renders differently. Each ref is fetched at most once
 * per session.
 */
export const useAtlasObjectPreviews = (
  refs: IAtlasObjectRef[],
): Record<string, IAtlasObjectPreview> => {
  const dispatch = useDispatch()
  const { byKey, requestedKeys } = useSelector(atlasObjectPreviews)

  // Stable across renders for an unchanged set of links, so the effect below
  // doesn't re-run on every keystroke in the editor that owns them.
  const missingKey = refs
    .map(atlasObjectRefKey)
    .filter((key) => !byKey[key] && !requestedKeys[key])
    .sort()
    .join(',')

  const missingRefs = useMemo(
    () =>
      missingKey
        ? missingKey.split(',').map((key) => {
            const separator = key.indexOf(':')
            return {
              type: key.slice(0, separator),
              id: key.slice(separator + 1),
            } as IAtlasObjectRef
          })
        : [],
    [missingKey],
  )

  useEffect(() => {
    if (!missingRefs.length) {
      return
    }
    missingRefs.forEach((ref) => pendingRefs.set(atlasObjectRefKey(ref), ref))
    scheduleFlush(dispatch)
  }, [dispatch, missingRefs])

  return byKey
}
